import { useCallback, useRef } from "react";

import { useMutation } from "@tanstack/react-query";

import { calculateAnonymousForecast } from "services";

import { MUTATE_ANONYMOUS_FORECAST, TIMEOUT_LONG_RUNNING } from "consts";

import { CalculateAnonymousForecastRequestBody } from "models";

// The pre-login forecast step renders its own error state, so the global error toast stays suppressed.
// react-query gives mutations no signal, so we hold the abort controller here.
export const useMutateAnonymousForecast = () => {
  const controllerRef = useRef<AbortController>();
  const dispatchedRunsRef = useRef(0);

  const abort = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = undefined;
  }, []);

  const mutation = useMutation({
    mutationKey: [MUTATE_ANONYMOUS_FORECAST],
    mutationFn: (body: CalculateAnonymousForecastRequestBody) => {
      abort();
      dispatchedRunsRef.current += 1;
      const controller = new AbortController();
      // We do not register the first run. Nothing can abort it, so its result always reaches the cache.
      controllerRef.current = dispatchedRunsRef.current > 1 ? controller : undefined;
      return calculateAnonymousForecast(body, { signal: controller.signal, timeout: TIMEOUT_LONG_RUNNING, hideErrorMessage: true });
    },
  });

  return { ...mutation, abort };
};
